import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ChevronLeft, Utensils, AlertTriangle, RotateCcw, Printer, Shuffle, Repeat, ShieldCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  fromPersistable, toPersistable, osszeg, emakro, alkalmazottHozz,
  cserelFogas, cserelHozzavalo, cserePairok, NAPROV, bevasarloLista,
} from '../lib/mealGenerator';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'oc_access_token';
const hu = n => Math.round(n).toLocaleString('hu-HU');

// Deep clone of a runtime terv — mutating functions (`cserelFogas`,
// `cserelHozzavalo`) work in-place, so we clone before every call to keep
// React state immutable.
const cloneTerv = terv => terv.map(d => ({
  ...d,
  meals: d.meals.map(m => ({
    ...m,
    slot: m.slot,
    r: { ...m.r },
    cserek: { ...(m.cserek || {}) },
    koret: m.koret ? { ...m.koret } : null,
  })),
}));

export default function PortalMealPlanPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [planDoc, setPlanDoc] = useState(null);
  const [terv, setTerv] = useState([]);
  const [printMode, setPrintMode] = useState(false);
  // Watermark identity — pulled from /portal/me so leaked screenshots are
  // traceable back to the specific client. Falls back to an anonymous
  // "OROSZ COACHING" watermark if the me-call fails so the plan is never
  // rendered without ANY protective overlay.
  const [me, setMe] = useState(null);
  // Signals whether a print/PDF export was initiated through our controlled
  // handler. The `@media print` CSS uses this to keep the body visible ONLY
  // when we authorized it — an ad-hoc Ctrl+P prints a blank page instead.
  const printAllowedRef = useRef(false);

  const token = localStorage.getItem(TOKEN_KEY);
  // A Google-lel belépett ügyfélnek nincs JWT-je, csak session_token sütije —
  // ezért MINDEN portál hívás withCredentials-szel megy, a Bearer fejléc pedig
  // csak akkor kerül rá, ha van token. (Enélkül a Google-ös fiókban üresnek
  // látszott az étrend.)
  const authCfg = {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };

  useEffect(() => {
    axios.get(`${API}/api/portal/meal-plan`, authCfg)
      .then(r => {
        setPlanDoc(r.data.plan);
        setTerv(r.data.plan ? fromPersistable(r.data.plan.plan || []) : []);
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          window.location.assign('/fiokom');
          return;
        }
        toast.error('Nem sikerült betölteni az étrendet.');
      })
      .finally(() => setLoading(false));
    // Fire-and-forget identity fetch for the watermark.
    axios.get(`${API}/api/portal/me`, authCfg)
      .then(r => setMe(r.data?.user || null))
      .catch(() => { /* silent — watermark simply falls back */ });
  }, []);

  // ------------------------------------------------------------------------
  // Anti-piracy shield — blocks the most common casual leak channels:
  //   * right-click / long-press menu
  //   * drag & drop of images
  //   * text selection
  //   * Ctrl+P / Ctrl+S / Ctrl+A / Ctrl+C / PrintScreen keyboard shortcuts
  // Full screenshot blocking is not technically possible from a webpage
  // (any OS-level capture bypasses the DOM), so we complement this with a
  // per-user watermark and a backend download rate limit.
  // ------------------------------------------------------------------------
  useEffect(() => {
    const blockCtx = (e) => { e.preventDefault(); return false; };
    const blockDrag = (e) => { e.preventDefault(); return false; };
    const blockKey = (e) => {
      const k = (e.key || '').toLowerCase();
      const ctrlOrMeta = e.ctrlKey || e.metaKey;
      if (k === 'printscreen') { e.preventDefault(); toast.info('A képernyőmentés nem elérhető ezen az oldalon.'); return false; }
      if (ctrlOrMeta && ['p', 's', 'c', 'a', 'x'].includes(k)) {
        e.preventDefault();
        if (k === 'p') toast.info('Használd a "PDF letöltés" gombot — a böngésző Ctrl+P nem elérhető.');
        return false;
      }
    };
    document.addEventListener('contextmenu', blockCtx);
    document.addEventListener('dragstart', blockDrag);
    document.addEventListener('keydown', blockKey);
    return () => {
      document.removeEventListener('contextmenu', blockCtx);
      document.removeEventListener('dragstart', blockDrag);
      document.removeEventListener('keydown', blockKey);
    };
  }, []);

  const persist = async (nextTerv, successMsg) => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/api/portal/meal-plan`,
        { plan: toPersistable(nextTerv) },
        authCfg,
      );
      setPlanDoc(res.data.plan);
      if (successMsg) toast.success(successMsg);
    } catch (e) {
      toast.error('Mentés sikertelen: ' + (e?.response?.data?.detail || e.message));
      // Roll back to persisted plan on failure.
      if (planDoc) setTerv(fromPersistable(planDoc.plan || []));
    }
    setSaving(false);
  };

  const swapMeal = async (di, mi) => {
    const goalHu = { fat_loss: 'Fogyás', maintenance: 'Szintentartás', muscle_gain: 'Izomépítés' }[planDoc.meta?.goal] || 'Szintentartás';
    const cfg = planDoc.meta?.filters || {
      diet: { veget: false, vegan: false, laktoz: false },
      konyhak: [], feherjedus: false, edzes: [false, false, false, false, false, false, false],
    };
    const next = cloneTerv(terv);
    cserelFogas(next, di, mi, goalHu, cfg);
    setTerv(next);
    await persist(next, 'Fogás lecserélve, kalóriahű.');
  };

  const swapIngredient = async (di, mi, volt) => {
    const next = cloneTerv(terv);
    cserelHozzavalo(next, di, mi, volt);
    setTerv(next);
    await persist(next); // silent — toggle is high-frequency
  };

  const resetAll = async () => {
    if (!window.confirm('Biztos visszaállítod az eredeti étrendet? Minden saját cseréd elveszik.')) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/portal/meal-plan/reset`, {}, authCfg);
      setPlanDoc(res.data.plan);
      setTerv(fromPersistable(res.data.plan.plan || []));
      toast.success('Étrend visszaállítva az eredetire.');
    } catch (e) {
      toast.error('Visszaállítás sikertelen.');
    }
    setSaving(false);
  };

  const doPrint = async () => {
    // Hit the rate-limit tick first — a 429 aborts the print with a friendly
    // toast so the client can't bypass the counter by cancelling the print
    // dialog (the tick has already recorded the intent).
    try {
      const r = await axios.post(
        `${API}/api/portal/meal-plan/download-tick`, {},
        authCfg,
      );
      const remaining = r?.data?.remaining;
      if (typeof remaining === 'number') {
        toast.success(
          remaining > 0
            ? `PDF letöltés engedélyezve. ${remaining} letöltés maradt az elkövetkező 5 percben.`
            : 'PDF letöltés engedélyezve — ez volt az utolsó a mostani 5 perces ablakban.',
        );
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Letöltés jelenleg nem elérhető, próbáld később.';
      toast.error(detail);
      return;
    }
    printAllowedRef.current = true;
    setPrintMode(true);
    // Give React one tick to render the print layout before the print dialog.
    setTimeout(() => {
      window.print();
      setTimeout(() => { setPrintMode(false); printAllowedRef.current = false; }, 500);
    }, 100);
  };

  const napi = useMemo(() => terv.map(d => osszeg(d.meals)), [terv]);
  const bevasarlo = useMemo(() => bevasarloLista(terv), [terv]);
  // Melyik hét listája látszik ('all' = teljes időszak). Egy hét esetén nincs fül.
  const [shopWeek, setShopWeek] = useState('0');
  const shoppingSections = useMemo(() => {
    if (!bevasarlo.multiWeek) {
      return [{ key: 'single', cim: '', groups: bevasarlo.groups, total: bevasarlo.total, napok: terv.length }];
    }
    // Nyomtatásban minden hét + a teljes összesítés egymás után.
    if (printMode) {
      return [
        ...bevasarlo.weeks.map((w) => ({ key: String(w.het), cim: w.cim, groups: w.groups, total: w.total, napok: w.napok })),
        { key: 'all', cim: 'Teljes időszak összesen', groups: bevasarlo.groups, total: bevasarlo.total, napok: terv.length },
      ];
    }
    if (shopWeek === 'all') {
      return [{ key: 'all', cim: 'Teljes időszak összesen', groups: bevasarlo.groups, total: bevasarlo.total, napok: terv.length }];
    }
    const w = bevasarlo.weeks.find((x) => String(x.het) === shopWeek) || bevasarlo.weeks[0];
    return [{ key: String(w.het), cim: w.cim, groups: w.groups, total: w.total, napok: w.napok }];
  }, [bevasarlo, shopWeek, printMode, terv.length]);
  const havi = useMemo(() => {
    if (!terv.length) return null;
    const n = terv.length;
    const s = napi.reduce((a, o) => ({ kcal: a.kcal + o.kcal, p: a.p + o.p, ch: a.ch + o.ch, zs: a.zs + o.zs }),
      { kcal: 0, p: 0, ch: 0, zs: 0 });
    return { kcal: s.kcal / n, p: s.p / n, ch: s.ch / n, zs: s.zs / n };
  }, [terv, napi]);

  // Detect whether the client has any active swaps — used to enable the reset button.
  const hasCustomChanges = useMemo(() => {
    if (!planDoc?.original_plan) return false;
    return JSON.stringify(planDoc.plan) !== JSON.stringify(planDoc.original_plan);
  }, [planDoc]);

  // Watermark text — client's email is the strongest deterrent (a leaked
  // screenshot would show whose account leaked it). Fall back to name +
  // brand if the me-call did not populate.
  const wmLabel = (me?.email || me?.name || 'OROSZ COACHING · info@oroszcoaching.hu').toUpperCase();
  const wmSvg = encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='460' height='260'>
       <text x='230' y='140' text-anchor='middle' transform='rotate(-24 230 130)'
             fill='#000' font-family='Montserrat, Arial, sans-serif' font-size='16' font-weight='700'>
         ${wmLabel}
       </text>
     </svg>`
  );
  const wmBg = { backgroundImage: `url("data:image/svg+xml;utf8,${wmSvg}")` };

  // Toggle a class on <html> that unlocks @media print — set only right
  // before window.print() and cleared after. Anything else (native Ctrl+P,
  // browser menu "Print") sees the class missing and gets a blank page.
  // Must live before any early return so hook order stays stable.
  useEffect(() => {
    const el = document.documentElement;
    if (printMode && printAllowedRef.current) {
      el.classList.add('print-ok');
    } else {
      el.classList.remove('print-ok');
    }
    return () => el.classList.remove('print-ok');
  }, [printMode]);

  if (loading) {
    return <div className="min-h-screen bg-gray-950 flex items-center justify-center text-white">
      <Loader2 className="animate-spin" /></div>;
  }

  const printCss = `
    /* Anti-piracy CSS: block casual copy / drag / mobile long-press save. */
    .plan-shield-root {
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;
      -webkit-touch-callout: none;
      -webkit-tap-highlight-color: transparent;
    }
    .plan-shield-root img {
      pointer-events: none;
      -webkit-user-drag: none;
      user-drag: none;
    }
    /* Watermark overlay — repeating pattern of the client's email tiled
       across the plan area. Rotated + faint so it stays out of the way but
       shows up cleanly on any casual screenshot. */
    .plan-watermark {
      position: absolute;
      inset: 0;
      pointer-events: none;
      z-index: 40;
      overflow: hidden;
      opacity: 0.06;
      background-repeat: repeat;
      background-position: 0 0;
      background-size: 460px 260px;
      mix-blend-mode: normal;
    }
    /* Ad-hoc browser print (Ctrl+P) prints a blank page unless we
       authorized it via the "PDF letöltés" button (see printAllowedRef). */
    @media print {
      html:not(.print-ok) body { display: none !important; }
      @page { size: A4; margin: 12mm; }
      .no-print { display: none !important; }
      .plan-watermark { opacity: 0.10 !important; z-index: 999 !important; }
      body, .print-root { background: white !important; color: #111 !important; }
      .print-card { background: white !important; border: 1px solid #ddd !important; page-break-inside: avoid; }
      .print-title { color: #111 !important; }
      .print-muted { color: #555 !important; }
      .print-accent { color: #b8860b !important; }
      details { display: block; }
      details > summary { list-style: none; pointer-events: none; }
      details[open] > * { display: block; }
    }
  `;

  return (
    <div
      className={`min-h-screen ${printMode ? 'bg-white text-gray-900' : 'bg-gray-950 text-white'} print-root plan-shield-root relative`}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
    >
      <style>{printCss}</style>

      <header className="border-b border-gray-800 bg-gray-900/80 backdrop-blur sticky top-0 z-10 no-print">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/fiokom" className="text-gray-400 hover:text-white"><ChevronLeft size={20} /></Link>
            <Utensils className="text-yellow-500" size={20} />
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>Étrendem</h1>
          </div>
          {planDoc && (
            <div className="flex items-center gap-2">
              {hasCustomChanges && (
                <Button
                  data-testid="meal-plan-reset"
                  onClick={resetAll}
                  disabled={saving}
                  variant="outline"
                  size="sm"
                  className="text-amber-300 border-amber-500/40 hover:bg-amber-500/10"
                >
                  <RotateCcw size={14} className="mr-1.5" /> Vissza az eredetihez
                </Button>
              )}
              <Button
                data-testid="meal-plan-print"
                onClick={doPrint}
                size="sm"
                className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold"
              >
                <Printer size={14} className="mr-1.5" /> PDF / Nyomtatás
              </Button>
            </div>
          )}
        </div>
      </header>

      <main className={`container mx-auto ${printMode ? 'px-0 py-4' : 'px-6 py-8'} max-w-4xl relative`}>
        {/* Full-plan watermark — always rendered so a screenshot anywhere on
            the page carries the identity marker. */}
        <div className="plan-watermark" style={wmBg} aria-hidden="true" />

        {/* Anti-piracy notice — the deterrent is as important as the tech. */}
        {planDoc && (
          <div
            data-testid="meal-plan-shield-banner"
            className={`no-print mb-5 rounded-lg border ${printMode ? 'hidden' : ''} border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-3`}
          >
            <ShieldCheck className="text-amber-400 mt-0.5 flex-shrink-0" size={18} />
            <div className="text-xs md:text-[13px] text-amber-100/90 leading-relaxed">
              <span className="font-bold text-amber-200">Ez a mintaétrend személyre szólóan téged illet.</span>{' '}
              Szerzői jog védi — csak saját használatra. A tartalom másolása,
              lementése vagy megosztása harmadik féllel a szerzői jogi törvénybe
              ütközik. A PDF letöltés visszatartó okokból korlátozott
              (max. 3 letöltés / 5 perc).
            </div>
          </div>
        )}
        {!planDoc ? (
          <div className="bg-gray-900 border border-dashed border-gray-700 rounded-lg p-16 text-center text-gray-500">
            <Utensils size={40} className="mx-auto mb-4 opacity-50" />
            <p>Még nincs számodra étrend feltöltve.</p>
            <p className="text-xs mt-2">Ha rendelted a coaching csomagot, az étrend rövidesen elérhető lesz itt.</p>
          </div>
        ) : (
          <>
            {/* Print-only header. */}
            {printMode && (
              <div className="mb-4 border-b pb-3">
                <div className="text-xs uppercase tracking-widest print-accent">Orosz Coaching</div>
                <div className="text-xl font-bold print-title">{planDoc.title}</div>
                <div className="text-xs print-muted mt-1">Készítette: Orosz Gábor · oroszcoaching.hu</div>
              </div>
            )}

            <div
              data-testid="meal-plan-medical-disclaimer"
              className={`mb-6 rounded-lg border p-4 flex items-start gap-3 ${
                printMode ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-amber-300 bg-amber-50 text-amber-900'
              }`}
            >
              <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm leading-relaxed">
                <strong>Fontos tudnivaló.</strong> Ez az étrend <strong>általános mintaétrend</strong>, amit a
                megadott makrócélokra (kalória, fehérje, szénhidrát, zsír) generáltunk. Ez <strong>nem
                dietetikusi tanácsadás</strong> és <strong>nem helyettesíti az orvosi vagy szakorvosi
                szakvéleményt</strong>. Krónikus betegség, ételallergia, ételintolerancia, terhesség,
                szoptatás vagy bármilyen gyógyszeres kezelés esetén az étrend bevezetése előtt
                <strong> kérj konzultációt a kezelőorvosodtól vagy okleveles dietetikustól</strong>.
              </div>
            </div>

            {!printMode && (
              <div className="mb-6">
                <h2 className="text-2xl font-bold">{planDoc.title}</h2>
                <div className="text-sm text-gray-400 mt-1">
                  Célok: <span className="text-white">{planDoc.meta?.target?.kcal} kcal</span>
                  {' · '}Fehérje <span className="text-white">{planDoc.meta?.target?.protein_g}g</span>
                  {' · '}Szénhidrát <span className="text-white">{planDoc.meta?.target?.carbs_g}g</span>
                  {' · '}Zsír <span className="text-white">{planDoc.meta?.target?.fat_g}g</span>
                </div>
              </div>
            )}

            {havi && (
              <div className={`rounded-lg border p-4 grid grid-cols-4 gap-3 text-center mb-6 ${
                printMode
                  ? 'border-gray-300 bg-gray-50'
                  : 'border-yellow-500/30 bg-gradient-to-br from-yellow-500/10 to-emerald-500/10'
              }`}>
                {[['Napi átlag kcal', hu(havi.kcal)],
                  ['Fehérje g', hu(havi.p)],
                  ['Szénhidrát g', hu(havi.ch)],
                  ['Zsír g', hu(havi.zs)]].map(([label, v]) => (
                  <div key={label}>
                    <div className={`text-xs ${printMode ? 'print-muted' : 'text-gray-400'}`}>{label}</div>
                    <div className={`text-lg font-bold ${printMode ? 'print-title' : 'text-white'}`}>{v}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-4">
              {terv.map((d, di) => (
                <div
                  key={di}
                  className={`rounded-lg p-4 print-card ${printMode ? 'border border-gray-300 bg-white' : 'bg-gray-900 border border-gray-800'}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className={`text-xs uppercase tracking-wider ${printMode ? 'print-muted' : 'text-gray-400'}`}>
                        {planDoc.meta?.mode === 'honap' ? `${d.het + 1}. hét · ` : ''}
                        {planDoc.meta?.mode === 'nap' ? '1. nap' : `${NAPROV[d.i]} nap`}
                      </span>
                      {d.edzes && (
                        <span className={`ml-2 px-2 py-0.5 text-xs rounded ${
                          printMode ? 'bg-emerald-100 text-emerald-800' : 'bg-emerald-600/20 text-emerald-400'
                        }`}>edzésnap</span>
                      )}
                    </div>
                    <div className={`text-xs ${printMode ? 'print-muted' : 'text-gray-500'}`}>{hu(napi[di].kcal)} kcal</div>
                  </div>

                  <div className="space-y-3">
                    {d.meals.map((m, mi) => {
                      const em = emakro(m);
                      const kcal = em.kcal * m.f + (m.koret ? m.koret.kcal * m.koret.g / 100 : 0);
                      const parok = cserePairok(m.r.helyett);
                      const ingredientList = alkalmazottHozz(m);
                      return (
                        <div
                          key={mi}
                          className={`rounded p-3 print-card ${printMode ? 'border border-gray-200 bg-gray-50' : 'bg-gray-800/50'}`}
                        >
                          <div className="flex items-start justify-between gap-3 mb-1">
                            <div className="flex-1 min-w-0">
                              <div className={`text-xs font-semibold uppercase tracking-wider ${printMode ? 'print-accent' : 'text-yellow-500'}`}>
                                {m.slot.cim}
                              </div>
                              <div className={`text-sm font-medium mt-1 ${printMode ? 'print-title' : 'text-white'}`}>
                                {m.r.nev}
                                {m.batch && <span className="ml-2 text-xs text-emerald-500">(dupla adag)</span>}
                                {m.marad && <span className="ml-2 text-xs text-blue-400">(maradék)</span>}
                              </div>
                            </div>
                            <div className={`text-xs text-right whitespace-nowrap ${printMode ? 'print-muted' : 'text-gray-400'}`}>
                              <div>{hu(kcal)} kcal</div>
                              {!printMode && (
                                <Button
                                  data-testid={`swap-meal-${di}-${mi}`}
                                  onClick={() => swapMeal(di, mi)}
                                  disabled={saving}
                                  variant="ghost"
                                  size="sm"
                                  className="no-print mt-1 h-6 text-xs text-yellow-500 hover:text-yellow-300 hover:bg-yellow-500/10"
                                  title="Kalóriahű fogás-csere"
                                >
                                  <Shuffle size={12} className="mr-1" /> Csere
                                </Button>
                              )}
                            </div>
                          </div>

                          <div className={`text-xs mt-1 ${printMode ? 'text-gray-800' : 'text-gray-300'}`}>
                            <strong>Hozzávalók:</strong>{' '}
                            {ingredientList.map((x, idx) => (
                              <React.Fragment key={idx}>
                                {idx > 0 && ', '}
                                <span className={x.csereltE ? (printMode ? 'text-emerald-800 font-medium' : 'text-emerald-400') : ''}>
                                  {x.qty ? `${x.qty}${x.unit} ${x.nev}` : x.nev}
                                </span>
                              </React.Fragment>
                            ))}
                            {m.koret && (
                              <span className={printMode ? 'text-blue-700' : 'text-blue-300'}>
                                {' '} + {m.koret.g}g {m.koret.nev}
                              </span>
                            )}
                          </div>

                          {/* Ingredient swaps (from recipe's "helyett" field). */}
                          {!printMode && parok.length > 0 && (
                            <div className="mt-2 no-print flex flex-wrap gap-1.5">
                              {parok.map(p => {
                                const on = !!m.cserek?.[p.volt];
                                return (
                                  <button
                                    key={p.volt}
                                    data-testid={`swap-ing-${di}-${mi}-${p.volt}`}
                                    onClick={() => swapIngredient(di, mi, p.volt)}
                                    disabled={saving}
                                    className={`text-xs px-2 py-1 rounded border transition ${
                                      on
                                        ? 'bg-emerald-600 text-white border-emerald-600'
                                        : 'bg-gray-900 text-gray-300 border-gray-700 hover:border-emerald-500 hover:text-emerald-300'
                                    }`}
                                    title={on ? `Vissza: ${p.volt}` : `Cserélve: ${p.volt} → ${p.helyette}`}
                                  >
                                    <Repeat size={10} className="inline mr-1" />
                                    {p.volt} → {p.helyette}
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {m.r.elk && (
                            <details className={`text-xs mt-2 ${printMode ? 'text-gray-700' : 'text-gray-400'}`} open={printMode}>
                              <summary className={printMode ? '' : 'cursor-pointer hover:text-white'}>Elkészítés</summary>
                              <p className="mt-1 whitespace-pre-line">{m.r.elk}</p>
                            </details>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Bevásárlólista — hetekre bontva, ha több hétre szól a terv */}
            {bevasarlo.groups.length > 0 && (
              <div
                data-testid="portal-shopping-list"
                className={`mt-6 rounded-lg p-4 print-card ${printMode ? 'border border-gray-300 bg-white' : 'bg-gray-900 border border-gray-800'}`}
              >
                <div className="flex items-center justify-between gap-3 mb-1">
                  <div className={`text-sm font-bold ${printMode ? 'print-title' : 'text-white'}`}>
                    Bevásárlólista
                  </div>
                  <div className={`text-xs ${printMode ? 'print-muted' : 'text-gray-500'}`}>
                    {terv.length} napra · {bevasarlo.total} alapanyag
                  </div>
                </div>
                <p className={`text-xs mb-3 ${printMode ? 'print-muted' : 'text-gray-500'}`}>
                  A terv összes étkezésének alapanyaga összeadva, a cserékkel együtt. A fűszereket
                  és az ízlés szerinti hozzávalókat becsülve add hozzá.
                </p>

                {bevasarlo.multiWeek && !printMode && (
                  <div className="flex flex-wrap gap-2 mb-4 no-print" data-testid="shopping-week-tabs">
                    {[...bevasarlo.weeks.map((w) => ({ key: String(w.het), cim: w.cim, napok: w.napok })),
                      { key: 'all', cim: 'Teljes időszak', napok: terv.length }].map((t) => (
                      <button
                        key={t.key}
                        type="button"
                        data-testid={`shopping-week-tab-${t.key}`}
                        onClick={() => setShopWeek(t.key)}
                        className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                          shopWeek === t.key
                            ? 'bg-yellow-500 text-gray-950'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        {t.cim} <span className="opacity-60">({t.napok} nap)</span>
                      </button>
                    ))}
                  </div>
                )}

                {shoppingSections.map((sec) => (
                  <div key={sec.key} className="mb-5 last:mb-0">
                    {sec.cim && (
                      <div className={`text-xs font-bold uppercase tracking-wider mb-2 pb-1 border-b ${
                        printMode ? 'text-gray-900 border-gray-300' : 'text-white border-gray-800'
                      }`}>
                        {sec.cim} <span className={printMode ? 'print-muted' : 'text-gray-500'}>· {sec.napok} nap · {sec.total} alapanyag</span>
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                      {sec.groups.map((g) => (
                        <div key={g.cim}>
                          <div className={`text-[11px] font-bold uppercase tracking-wider mb-1.5 ${printMode ? 'print-accent' : 'text-yellow-500'}`}>
                            {g.cim}
                          </div>
                          <ul className="space-y-1">
                            {g.items.map((i) => (
                              <li
                                key={i.nev}
                                className={`text-xs flex items-baseline gap-2 ${printMode ? 'text-gray-800' : 'text-gray-300'}`}
                              >
                                <span className={printMode ? 'text-gray-400' : 'text-gray-600'}>☐</span>
                                <span className="flex-1">{i.nev}</span>
                                <span className={`whitespace-nowrap font-medium ${printMode ? 'text-gray-900' : 'text-white'}`}>
                                  {i.label}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {saving && (
              <div className="fixed bottom-6 right-6 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 flex items-center gap-2 text-sm text-gray-300 no-print">
                <Loader2 size={14} className="animate-spin" /> Mentés…
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
